import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEdit } from "react-icons/fi";
import { IoEyeOutline } from "react-icons/io5";
import MasterShell from "../common/MasterShell";
import MasterListPage from "../common/MasterListPage";
import MasterToast from "../common/MasterToast";
import StatusBadge from "../common/StatusBadge";
import useRouteToast from "../common/useRouteToast";
import { getDistricts } from "../../services/districtService";
import {
  extractApiErrorMessage,
  formatMasterDate,
  matchesSearch,
} from "../../utils/masterHelpers";
import { useCapabilities } from "../../contexts/CapabilitiesContext";
import "./Districts.css";

export default function DistrictList() {
  const navigate = useNavigate();
  const { clearToast, toast } = useRouteToast();
  const { canAny } = useCapabilities();
  const canCreateDistrict = canAny("districts", ["create", "manage"]);
  const canReadDistrict = canAny("districts", ["read", "manage"]);
  const canUpdateDistrict = canAny("districts", ["update", "manage"]);
  const [districts, setDistricts] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchDistricts = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getDistricts();
      setDistricts(Array.isArray(response) ? response : []);
    } catch (apiError) {
      setError(extractApiErrorMessage(apiError, "Failed to load districts."));
      setDistricts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDistricts();
  }, [fetchDistricts]);

  const columns = [
    {
      key: "rowNumber",
      label: "Sr. No.",
      render: (_, __, rowNumber) => rowNumber,
    },
    { key: "code", label: "Code" },
    { key: "name", label: "District Name" },
    { key: "state", label: "State" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge value={row.is_active} />,
    },
    {
      key: "created_at",
      label: "Created At",
      render: (row) => formatMasterDate(row.created_at),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="masters-table__actions">
          {canReadDistrict ? (
            <button
              className="masters-icon-btn masters-icon-btn--view"
              onClick={() => navigate(`/districts/${row.id}/view`)}
              type="button"
            >
              <IoEyeOutline />
            </button>
          ) : null}
          {canUpdateDistrict && (
            <button
              className="masters-icon-btn masters-icon-btn--edit"
              onClick={() => navigate(`/districts/${row.id}/edit`)}
              type="button"
            >
              <FiEdit />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <MasterShell pageClassName="districts-module">
      <MasterListPage
        addLabel={canCreateDistrict ? "Add District" : undefined}
        addPath={canCreateDistrict ? "/districts/add" : undefined}
        className="new_master-records-list"
        columns={columns}
        emptyMessage="District records will appear here after they are created."
        error={error}
        isLoading={loading}
        onRetry={fetchDistricts}
        rows={districts}
        searchPlaceholder="Search by code, district name, or state"
        searchPredicate={(row, searchTerm) =>
          matchesSearch([row.code, row.name, row.state], searchTerm)
        }
        title="Districts"
      />
      <MasterToast
        message={toast?.message || ""}
        onClose={clearToast}
        open={Boolean(toast)}
        severity={toast?.severity || "success"}
      />
    </MasterShell>
  );
}
